import { z } from 'zod';
import { castIdSchema } from './cast.js';
import { changesSchema } from './changes.js';
import { planningStoryProposalSchema } from './chat.js';
import { idSchema } from './common.js';
import { castRoleSchema } from './content.js';
import { castAssignmentIdSchema } from './generation.js';
/**
 * Replacing one of a module's cast across its built lessons, through review — CURR-48.
 * A request becomes a proposal listing every lesson and scene it changes; Apply writes
 * the drafts in one go. `docs/decisions/module-recast.md`.
 */
/** POST /curriculum/modules/:id/recasts */
export const requestRecastSchema = z
    .strictObject({
    assignmentId: castAssignmentIdSchema
        .optional()
        .describe('The assignment to recast. With `character`, not `request`.'),
    character: castIdSchema
        .optional()
        .describe('Who takes the part. With `assignmentId`.'),
    role: castRoleSchema
        .trim()
        .min(1, 'Name the role.')
        .optional()
        .describe('A new role for the part. Absent keeps the one it has.'),
    request: z
        .string()
        .trim()
        .min(1)
        .max(300)
        .optional()
        .describe('Fix with AI at module scope, in the author’s words: "Replace Kofi with Betty as Supplier throughout this module".'),
    storyRevision: z
        .number()
        .int()
        .min(0)
        .describe('The module story revision the author is looking at.'),
})
    .refine((body) => body.request === undefined
    ? body.assignmentId !== undefined && body.character !== undefined
    : body.assignmentId === undefined && body.character === undefined, 'Send a request, or an assignment and the character replacing it.')
    .describe('Ask to replace one of the module’s cast in every lesson.');
export const recastStatusSchema = z
    .enum(['PROPOSED', 'APPLIED', 'DISCARDED'])
    .describe('Waiting for Apply; written to the drafts; turned down.');
export const recastSceneSchema = z.strictObject({
    sceneId: idSchema,
    position: z.number().int().min(1).describe('1-based.'),
    changes: changesSchema.shape.changes,
});
export const recastLessonSchema = z.strictObject({
    lessonId: idSchema.describe('The built lesson.'),
    curriculumLessonId: z.string(),
    title: z.string(),
    published: z
        .boolean()
        .describe('Learners keep the published version until the module publishes.'),
    outside: z
        .boolean()
        .describe('Not in this module: it places a scene the recast rewrites, so it changes too.'),
    changes: changesSchema.shape.changes.describe('The lesson’s own fields: its cast, title and summary.'),
    scenes: z.array(recastSceneSchema).describe('Only the scenes that change.'),
});
export const recastPartSchema = z.strictObject({
    character: castIdSchema,
    role: castRoleSchema,
});
export const recastSchema = z
    .strictObject({
    id: idSchema,
    moduleId: idSchema,
    assignmentId: castAssignmentIdSchema,
    from: recastPartSchema,
    to: recastPartSchema,
    request: z.string().describe('What was asked, as the author asked it.'),
    status: recastStatusSchema,
    why: z.string().describe('What the rewriter did, in its words.'),
    lacks: z
        .array(z.string())
        .describe('Each pose, mood or reaction the new character cannot play, which the drafts drop.'),
    lessons: z.array(recastLessonSchema),
    createdAt: z.iso.datetime(),
    appliedAt: z.iso.datetime().optional(),
})
    .describe('A proposed replacement of one of a module’s cast.');
/** POST /curriculum/modules/:id/fixes */
export const fixModuleSchema = z
    .strictObject({
    request: z
        .string()
        .trim()
        .min(1)
        .max(300)
        .describe('In the author’s words: "build the story - David".'),
    storyRevision: z
        .number()
        .int()
        .min(0)
        .describe('The module story revision the author is looking at.'),
})
    .describe('Fix with AI at module scope. A replacement ("Replace Kofi with Betty as Supplier") proposes a recast; anything else drafts the story, figures and beats with the request as guidance — CURR-59.');
export const moduleFixSchema = z
    .strictObject({
    recast: recastSchema
        .nullable()
        .describe('The proposed recast, when the request was a replacement.'),
    story: planningStoryProposalSchema
        .nullable()
        .describe('Otherwise a drafted story, applied as the chat’s is: POST /curriculum/proposals/apply-story.'),
})
    .describe('What a module Fix with AI proposes. Exactly one is set.');
//# sourceMappingURL=recast.js.map